import { Section } from '@expo/html-elements'
import { useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import {
  ConfigurableNotificationDefinitionInputSchema,
  configurableNotificationProjectTypes,
  configurableNotificationRecipientRoles,
  type ConfigurableNotificationCondition,
  type ConfigurableNotificationDefinition,
  type ConfigurableNotificationDefinitionInput,
  type ConfigurableNotificationRecipient,
  type ConfigurableNotificationRule,
} from '@dpa/common/dist/configurableNotifications'

import { trpc } from '../../../App'
import { Card, CardBody, CardHeader } from '../../components/Card'
import { Col, Row } from '../../components/Row'
import { H2, H4, Text } from '../../components/TextComponents'
import { Icon, IconName } from '../../components/Icon'
import { LoadingScreen } from '../../components/LoadingScreen'
import { dsv } from '../../styles/defaults'
import { addPrefix } from '../../locales/translationKeys'
import { useAuthContext } from '../../hooks/context/useAuthContext'
import { useUserListContext } from '../../hooks/context/useUserListContext'

type RootRule = {
  type: 'group'
  operator: 'all' | 'any'
  conditions: ConfigurableNotificationRule[]
}

type NotificationForm = ConfigurableNotificationDefinitionInput & {
  id?: number
}

type TemplateField = 'subjectTemplate' | 'bodyTemplate'

type NotificationKeySource = Pick<ConfigurableNotificationDefinitionInput, 'notificationKey'>
type NotificationAkteField = Extract<ConfigurableNotificationCondition, { field: string }>['field']

const conditionTypes: Array<ConfigurableNotificationCondition['type']> = [
  'fieldFilled',
  'fieldChanged',
  'handoverEvent',
  'elapsedSinceField',
  'currentState',
  'projectTypeIs',
  'projectTypeIsNot',
]

function conditionTypeOptions(currentType: ConfigurableNotificationCondition['type']) {
  return conditionTypes.includes(currentType)
    ? conditionTypes
    : [currentType, ...conditionTypes]
}

function selectOptionsWithCurrentValue(value: string, options: string[]) {
  return options.includes(value) ? options : [value, ...options]
}

const recipientTypes: Array<ConfigurableNotificationRecipient['type']> = [
  'email',
  'user',
  'role',
  'referencedUser',
  'previousReferencedUser',
]

const defaultFields = ['PDB_Projektnummer']
const emptyFields: string[] = []
const fallbackProjectTypes = [...configurableNotificationProjectTypes]
const fallbackRecipientRoles = [...configurableNotificationRecipientRoles]

function handoverEventLabel(event: string) {
  switch (event) {
    case 'requested':
      return 'angefragt'
    case 'preapproved':
      return 'vorab angenommen'
    case 'approved':
      return 'angenommen'
    case 'denied':
      return 'abgelehnt'
    case 'revoked':
      return 'zurueckgezogen'
    default:
      return event
  }
}

const inputStyle: CSSProperties = {
  ...dsv.text,
  boxSizing: 'border-box',
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: dsv.colors.connectGrau30,
  backgroundColor: dsv.colors.connectGrau7,
  color: dsv.colors.connectGrau,
  padding: '9px 10px',
  minHeight: 38,
  minWidth: 0,
  maxWidth: '100%',
  width: '100%',
  fontSize: 14,
}

const readOnlyInputStyle: CSSProperties = {
  ...inputStyle,
  color: dsv.colors.connectGrau70,
  backgroundColor: dsv.colors.base2,
}

type NotificationSelectOption = {
  value: string
  label: string
  title?: string
}

const notificationSelectMenuProps = {
  PaperProps: {
    sx: {
      maxHeight: 360,
      border: `1px solid ${dsv.colors.connectGrau30}`,
      boxShadow: '0 8px 18px rgba(0, 0, 0, 0.16)',
    },
  },
}

const notificationSelectSx = {
  width: '100%',
  minWidth: 0,
  minHeight: 38,
  height: 38,
  backgroundColor: dsv.colors.connectGrau7,
  color: dsv.colors.connectGrau,
  fontFamily: dsv.text.fontFamily,
  fontSize: 14,
  '.MuiSelect-select': {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    minHeight: 'auto',
    padding: '8px 32px 8px 10px',
    fontFamily: dsv.text.fontFamily,
    fontSize: 14,
  },
  '.MuiOutlinedInput-notchedOutline': {
    borderColor: dsv.colors.connectGrau30,
  },
}

const smallButtonStyle: CSSProperties = {
  ...dsv.text,
  borderWidth: 0,
  backgroundColor: dsv.colors.connectGrau,
  color: dsv.colors.white,
  padding: '8px 11px',
  cursor: 'pointer',
  minHeight: 36,
}

const prominentButtonStyle: CSSProperties = {
  ...smallButtonStyle,
  fontSize: 16,
  padding: '10px 14px',
}

const textOnDarkStyle: CSSProperties = {
  ...dsv.text,
  color: dsv.colors.connectGrau7,
}

const headingOnDarkStyle: CSSProperties = {
  ...dsv.heading,
  color: dsv.colors.connectGrau7,
}

const fieldLabelStyle: CSSProperties = {
  ...textOnDarkStyle,
  display: 'block',
  marginBottom: 6,
  fontSize: 13,
  fontWeight: 700,
}

const activeToggleStyle: CSSProperties = {
  ...textOnDarkStyle,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 0,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
}

const activeCheckboxStyle: CSSProperties = {
  width: 18,
  height: 18,
  cursor: 'pointer',
}

const roleRequirementLabelStyle: CSSProperties = {
  ...textOnDarkStyle,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
}

const editFormWrapperStyle: CSSProperties = {
  boxSizing: 'border-box',
  width: '100%',
  borderWidth: 0,
  padding: 0,
  margin: 0,
  backgroundColor: dsv.colors.connectGrau,
}

const modalBackdropStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  zIndex: 1000,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  backgroundColor: 'rgba(0, 0, 0, 0.55)',
  padding: '32px 24px',
  overflowY: 'auto',
}

const modalDialogStyle: CSSProperties = {
  boxSizing: 'border-box',
  width: '100%',
  maxWidth: 1080,
  backgroundColor: dsv.colors.connectGrau,
  padding: 28,
  boxShadow: '0 18px 48px rgba(0, 0, 0, 0.45)',
}

const labelColumnStyle: CSSProperties = {
  minWidth: 0,
}

const formHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  padding: '18px 20px 16px',
  borderBottom: `1px solid ${dsv.colors.connectGrau70}`,
  boxShadow: `inset 0 4px 0 ${dsv.colors.GpJouleGelb}`,
}

const formBodyStyle: CSSProperties = {
  display: 'grid',
  gap: 22,
  padding: '18px 20px 20px',
}

const formSectionStyle: CSSProperties = {
  display: 'grid',
  gap: 10,
}

const sectionTitleStyle: CSSProperties = {
  ...headingOnDarkStyle,
  margin: 0,
  fontSize: 21,
}

const sectionToolbarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 10,
}

const formFooterStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 10,
  paddingTop: 14,
  borderTop: `1px solid ${dsv.colors.connectGrau70}`,
}

const twoColumnFormGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 230px) minmax(0, 1fr)',
  gap: 12,
}

const contentFormGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(190px, 260px)',
  gap: 12,
}

const tableHeaderCellStyle: CSSProperties = {
  ...dsv.headingFont,
  color: dsv.colors.connectGrau,
  padding: 10,
  textAlign: 'left',
}

const tableCellStyle: CSSProperties = {
  ...dsv.text,
  color: dsv.colors.connectGrau,
  padding: 10,
  borderBottom: `1px solid ${dsv.colors.connectGrau30}`,
  verticalAlign: 'top',
  fontSize: 14,
  lineHeight: '14px',
  height: 1,
}

const wrappingTableCellStyle: CSSProperties = {
  ...tableCellStyle,
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  lineHeight: '14px',
}

const wrappingTableTextStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  minHeight: '100%',
  height: '100%',
  fontSize: 14,
  lineHeight: '14px',
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
}

const notificationNameTextStyle: CSSProperties = {
  ...dsv.headingFont,
  display: 'flex',
  alignItems: 'center',
  minHeight: '100%',
  height: '100%',
  color: dsv.colors.connectGrau,
  fontSize: 14,
  lineHeight: '14px',
  margin: 0,
}

const notificationOverviewTableStyle: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  tableLayout: 'fixed',
  backgroundColor: dsv.colors.connectGrau7,
}

const tableCheckboxCellStyle: CSSProperties = {
  ...tableCellStyle,
  textAlign: 'center',
  verticalAlign: 'middle',
}

const tableCheckboxStyle: CSSProperties = {
  width: 18,
  height: 18,
  cursor: 'pointer',
}

const tableActionsStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 8,
}

const tableActionButtonStyle: CSSProperties = {
  justifyContent: 'center',
  marginRight: 0,
  width: '100%',
}

const triggerActionColumnWidth = 112

const triggerConditionsListStyle: CSSProperties = {
  display: 'grid',
  gap: 8,
}

const triggerConditionRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: `minmax(190px, 270px) minmax(220px, 1fr) minmax(170px, 280px) ${triggerActionColumnWidth}px`,
  gap: 10,
  alignItems: 'center',
  padding: 10,
  backgroundColor: 'rgba(255, 255, 255, 0.06)',
  borderLeft: `3px solid ${dsv.colors.connectGrau30}`,
}

const triggerConditionCellStyle: CSSProperties = {
  minWidth: 0,
}

const triggerActionCellStyle: CSSProperties = {
  minWidth: 0,
  width: triggerActionColumnWidth,
}

const tokenButtonListStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  marginTop: 10,
}

const tokenInsertButtonStyle: CSSProperties = {
  ...dsv.text,
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: dsv.colors.connectGrau30,
  backgroundColor: dsv.colors.connectGrau7,
  color: dsv.colors.connectGrau,
  padding: '8px 10px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
}

const recipientRowsStyle: CSSProperties = {
  display: 'grid',
  gap: 8,
}

const recipientRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: `minmax(170px, 290px) minmax(220px, 1fr) ${triggerActionColumnWidth}px`,
  gap: 10,
  alignItems: 'center',
  padding: 10,
  backgroundColor: 'rgba(255, 255, 255, 0.06)',
  borderLeft: `3px solid ${dsv.colors.connectGrau30}`,
}

const previewBoxStyle: CSSProperties = {
  marginTop: 14,
  padding: 14,
  backgroundColor: dsv.colors.connectGrau7,
  color: dsv.colors.connectGrau,
}

const fieldPickerDialogStyle: CSSProperties = {
  ...modalDialogStyle,
  maxWidth: 900,
  padding: 32,
}

const fieldPickerListStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
  gap: 8,
  maxHeight: '55vh',
  overflowY: 'auto',
  paddingRight: 6,
  marginTop: 12,
}

const fieldPickerButtonStyle: CSSProperties = {
  ...tokenInsertButtonStyle,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 4,
  minWidth: 0,
  whiteSpace: 'normal',
  textAlign: 'left',
}

const fieldPickerFieldNameStyle: CSSProperties = {
  ...dsv.text,
  color: dsv.colors.connectGrau70,
  fontSize: 12,
  wordBreak: 'break-all',
}

function asRootRule(rule: ConfigurableNotificationRule): RootRule {
  if (rule.type === 'group') {
    return rule
  }
  return {
    type: 'group',
    operator: 'all',
    conditions: [rule],
  }
}

function fieldLabel(t: (key: string) => string, field: string) {
  const key = addPrefix('labels', field)
  const translated = t(key)
  return translated === key ? field : translated
}

function enumLabel(t: (key: string) => string, value: string) {
  const translated = t(value)
  return translated === value ? value : translated
}

function translatedLabel(t: (key: string) => string, key: string, fallback: string) {
  const translated = t(key)
  return translated === key ? fallback : translated
}

function handoverLabel(t: (key: string) => string, handover: string) {
  if (handover === 'SuW2Running') {
    return `${translatedLabel(t, 'uebergabeTitle_SuW2Running', 'In Betrieb')} / ${translatedLabel(
      t,
      'uebergabeTitle_SuW2Running_warranty',
      'Gewaehrleistungsuebergabe'
    )}`
  }

  return translatedLabel(t, `uebergabeTitle_${handover}`, roleLabel(handover))
}

function sortFieldsByLabel(fields: string[], t: (key: string) => string) {
  return [...fields].sort((left, right) => (
    fieldLabel(t, left).localeCompare(fieldLabel(t, right), 'de', { sensitivity: 'base' }) ||
    left.localeCompare(right, 'de', { sensitivity: 'base' })
  ))
}

function compactOptionLabel(label: string, maxLength = 42) {
  if (label.length <= maxLength) {
    return label
  }
  const endLength = 16
  const startLength = maxLength - endLength - 3
  return `${label.slice(0, startLength)}...${label.slice(-endLength)}`
}

function templateTokenLabel(token: string) {
  switch (token) {
    case '{{project.id}}':
      return 'Projekt ID'
    case '{{project.link}}':
      return 'Projekt Link'
    case '{{project.title}}':
      return 'Projekttitel'
    case '{{project.number}}':
      return 'Projektnummer'
    case '{{project.state}}':
      return 'Projektstatus'
    case '{{project.lastTransition}}':
      return 'Letzter Statuswechsel'
    case '{{transition.comment}}':
      return 'Kommentar des Statuswechsels'
    case '{{changed.field}}':
      return 'Geaendertes Feld'
    case '{{changed.oldValue}}':
      return 'Alter Wert'
    case '{{changed.newValue}}':
      return 'Neuer Wert'
    case '{{field.FIELD_NAME}}':
      return 'Projektfeld'
    default:
      return token
  }
}

function formatDate(value: Date | string | null | undefined) {
  if (value == undefined) {
    return '-'
  }
  const date = value instanceof Date ? value : new Date(value)
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
}

function conditionLabel(type: ConfigurableNotificationCondition['type']) {
  switch (type) {
    case 'fieldFilled':
      return 'Feld ist gefuellt'
    case 'fieldEmpty':
      return 'Feld ist leer'
    case 'fieldEquals':
      return 'Feld hat Wert'
    case 'fieldChanged':
      return 'Feld wurde geaendert'
    case 'fieldChangedToFilled':
      return 'Feld wurde gefuellt'
    case 'fieldChangedToValue':
      return 'Feld wurde auf Wert gesetzt'
    case 'handoverEvent':
      return 'Uebergabe Ereignis'
    case 'elapsedSinceField':
      return 'Zeit seit Felddatum'
    case 'currentState':
      return 'Aktueller Status'
    case 'projectTypeIs':
      return 'Projektart ist'
    case 'projectTypeIsNot':
      return 'Projektart ist nicht'
  }
}

function recipientLabel(type: ConfigurableNotificationRecipient['type']) {
  switch (type) {
    case 'email':
      return 'E-Mail'
    case 'user':
      return 'Benutzer'
    case 'role':
      return 'Rolle'
    case 'referencedUser':
      return 'Projekt-Benutzerfeld'
    case 'previousReferencedUser':
      return 'Vorheriges Benutzerfeld'
  }
}

function roleLabel(role: string) {
  switch (role) {
    case 'Vertrieb2PE':
      return 'Vertrieb an Projektentwicklung'
    case 'PE2Vertrieb':
      return 'Projektentwicklung an Vertrieb'
    case 'Vertrieb2ENG':
      return 'Vertrieb an Engineering'
    case 'ENG2Vertrieb':
      return 'Engineering an Vertrieb'
    case 'CompletionOfPlanning':
      return 'Abschluss Planung'
    case 'CompletionOfPlanning2Vertrieb':
      return 'Abschluss Planung an Vertrieb'
    case 'Vertrieb2Projektleitung':
      return 'Vertrieb an Projektleitung'
    case 'Sales':
      return 'Sales (Vertrieb + Innendienst)'
    case 'ProjektSupport':
      return 'Projekt Support'
    case 'Projektierung':
      return 'Engineering'
    case 'PMO':
      return 'Projektleitung'
    case 'PE':
      return 'Projektentwicklung'
    case 'ServiceUndWartung':
      return 'Service & Wartung'
    case 'SW':
      return 'SW'
    case 'preliminaryHandover_Projektleitung2SuW':
      return 'Vorlaeufige Uebergabe an Team Betrieb'
    case 'finalHandover_Projektleitung2SuW':
      return 'Finale Uebergabe an Team Betrieb'
    case 'SuW2Running':
      return 'Betrieb/Fremdbetrieb'
    default:
      return role
  }
}

function mergeRecipientRoles(roles: string[]) {
  const merged: string[] = [...fallbackRecipientRoles]
  roles.forEach((role) => {
    if (!merged.includes(role)) {
      merged.push(role)
    }
  })
  return merged
}

function asNotificationAkteField(field: string): NotificationAkteField {
  return field as NotificationAkteField
}

function defaultCondition(field: string): ConfigurableNotificationCondition {
  return {
    type: 'fieldFilled',
    field: asNotificationAkteField(field),
  }
}

function conditionForType(
  type: ConfigurableNotificationCondition['type'],
  field: string
): ConfigurableNotificationCondition {
  const akteField = asNotificationAkteField(field)
  if (
    type === 'fieldFilled' ||
    type === 'fieldEmpty' ||
    type === 'fieldChanged' ||
    type === 'fieldChangedToFilled'
  ) {
    return { type, field: akteField }
  }
  if (type === 'fieldEquals' || type === 'fieldChangedToValue') {
    return { type, field: akteField, value: '' }
  }
  if (type === 'handoverEvent') {
    return { type, handover: 'Vertrieb2PE', event: 'requested' }
  }
  if (type === 'elapsedSinceField') {
    return { type, field: akteField, amount: 3, unit: 'days' }
  }
  if (type === 'currentState') {
    return { type, state: 'workOn_Vertrieb2PE', amount: 0, unit: 'days' }
  }
  return { type, projectType: configurableNotificationProjectTypes[0] }
}

function defaultRecipient(): ConfigurableNotificationRecipient {
  return {
    type: 'role',
    role: 'Admin',
    requireTeamleitung: false,
  }
}

function recipientForType(type: ConfigurableNotificationRecipient['type']): ConfigurableNotificationRecipient {
  if (type === 'email') {
    return { type, email: '' }
  }
  if (type === 'user') {
    return { type, userId: 1 }
  }
  if (type === 'role') {
    return { type, role: 'Admin', requireTeamleitung: false }
  }
  if (type === 'referencedUser') {
    return { type, field: 'PDB_VertriebsmitarbeiterId' }
  }
  return { type, field: 'PDB_VertriebsmitarbeiterId' }
}

function nextNotificationKey(definitions: NotificationKeySource[] | undefined) {
  const existingKeys = new Set((definitions ?? []).map((definition) => definition.notificationKey))
  const nextNumberFromExistingKeys = [...existingKeys]
    .filter((key) => /^\d+$/.test(key))
    .map(Number)
    .reduce((highest, value) => Math.max(highest, value), 0) + 1

  let nextNumber = nextNumberFromExistingKeys
  let notificationKey = String(nextNumber).padStart(2, '0')

  while (existingKeys.has(notificationKey)) {
    nextNumber += 1
    notificationKey = String(nextNumber).padStart(2, '0')
  }

  return notificationKey
}

function emptyForm(defaultField: string, notificationKey = nextNotificationKey(undefined)): NotificationForm {
  return {
    notificationKey,
    name: 'Neue Benachrichtigung',
    enabled: true,
    trigger: {
      type: 'group',
      operator: 'all',
      conditions: [defaultCondition(defaultField)],
    },
    recipients: [defaultRecipient()],
    subjectTemplate: 'Benachrichtigung fuer Projekt {{project.number}}',
    bodyTemplate: 'Projekt: {{project.title}}\n\n{{project.link}}',
  }
}

function ruleSummary(rule: ConfigurableNotificationRule, t: (key: string) => string): string {
  if (rule.type === 'group') {
    const joiner = rule.operator === 'all' ? ' UND ' : ' ODER '
    return rule.conditions.map((condition) => ruleSummary(condition, t)).join(joiner)
  }
  if ('field' in rule) {
    const field = fieldLabel(t, rule.field)
    if (rule.type === 'fieldFilled') {
      return `${field} ist gefuellt`
    }
    if (rule.type === 'fieldEmpty') {
      return `${field} ist leer`
    }
    if (rule.type === 'fieldEquals') {
      return `${field} = ${rule.value}`
    }
    if (rule.type === 'fieldChanged') {
      return `${field} wurde geaendert`
    }
    if (rule.type === 'fieldChangedToFilled') {
      return `${field} wurde gefuellt`
    }
    if (rule.type === 'fieldChangedToValue') {
      return `${field} wurde auf ${rule.value} gesetzt`
    }
    return `${rule.amount} ${rule.unit} seit ${field}`
  }
  if (rule.type === 'handoverEvent') {
    return `${handoverLabel(t, rule.handover)}: ${handoverEventLabel(rule.event)}`
  }
  if (rule.type === 'currentState') {
    const amount = rule.amount ?? 0
    if (amount > 0) {
      return `Status = ${rule.state} seit ${amount} ${rule.unit ?? 'days'}`
    }
    return `Status = ${rule.state}`
  }
  if (rule.type === 'projectTypeIs') {
    return `Projektart = ${enumLabel(t, rule.projectType)}`
  }
  return `Projektart != ${enumLabel(t, rule.projectType)}`
}

function recipientsSummary(
  recipients: ConfigurableNotificationRecipient[],
  userById: { [id in number]: { name: string } } | undefined,
  t: (key: string) => string
) {
  return recipients
    .map((recipient) => {
      if (recipient.type === 'email') {
        return recipient.email
      }
      if (recipient.type === 'user') {
        return userById?.[recipient.userId]?.name ?? `User ${recipient.userId}`
      }
      if (recipient.type === 'role') {
        return `Rolle ${roleLabel(recipient.role)}${recipient.requireTeamleitung === true ? ' (Teamadmin)' : ''}`
      }
      return fieldLabel(t, recipient.field)
    })
    .join(', ')
}

function notificationUpdateInput(definition: ConfigurableNotificationDefinition, enabled: boolean) {
  return {
    id: definition.id,
    notificationKey: definition.notificationKey,
    name: definition.name,
    enabled,
    trigger: definition.trigger,
    recipients: definition.recipients,
    subjectTemplate: definition.subjectTemplate,
    bodyTemplate: definition.bodyTemplate,
  }
}

function ActionButton({
  icon,
  children,
  onClick,
  danger = false,
  primary = false,
  prominent = false,
  style,
}: {
  icon: IconName
  children: string
  onClick: () => void
  danger?: boolean
  primary?: boolean
  prominent?: boolean
  style?: CSSProperties
}) {
  const buttonBaseStyle = prominent ? prominentButtonStyle : smallButtonStyle
  const foregroundColor = primary ? dsv.colors.connectGrau : dsv.colors.white

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...buttonBaseStyle,
        backgroundColor: primary
          ? dsv.colors.GpJouleGelb
          : danger
            ? dsv.colors.error
            : dsv.colors.connectGrau,
        color: foregroundColor,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        marginRight: 6,
        ...style,
      }}
    >
      <Icon name={icon} size={prominent ? 20 : 15} color={foregroundColor} />
      {children}
    </button>
  )
}

function NotificationSelect({
  value,
  options,
  onChange,
  style,
}: {
  value: string
  options: NotificationSelectOption[]
  onChange: (value: string) => void
  style?: CSSProperties
}) {
  return (
    <Select
      value={value}
      onChange={(event: SelectChangeEvent<string>) => onChange(event.target.value)}
      MenuProps={notificationSelectMenuProps}
      sx={{
        ...notificationSelectSx,
        width: style?.width ?? notificationSelectSx.width,
      }}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value} title={option.title} sx={{ fontSize: 14 }}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  )
}

function FieldSelect({
  value,
  fields,
  onChange,
  t,
}: {
  value: string
  fields: string[]
  onChange: (value: string) => void
  t: (key: string) => string
}) {
  return (
    <NotificationSelect
      value={value}
      onChange={onChange}
      options={fields.map((field) => {
        const label = fieldLabel(t, field)
        return { value: field, label: compactOptionLabel(label), title: label }
      })}
    />
  )
}

function ConditionEditor({
  condition,
  fields,
  states,
  projectTypes,
  handovers,
  handoverEvents,
  onChange,
  onRemove,
  t,
}: {
  condition: ConfigurableNotificationRule
  fields: string[]
  states: string[]
  projectTypes: string[]
  handovers: string[]
  handoverEvents: string[]
  onChange: (condition: ConfigurableNotificationRule) => void
  onRemove: () => void
  t: (key: string) => string
}) {
  if (condition.type === 'group') {
    return null
  }

  const timeUnitOptions = [
    { value: 'hours', label: 'Stunden' },
    { value: 'days', label: 'Tage' },
    { value: 'weeks', label: 'Wochen' },
  ]

  return (
    <div style={triggerConditionRowStyle}>
      <div style={triggerConditionCellStyle}>
        <NotificationSelect
          value={condition.type}
          onChange={(type) => onChange(
            conditionForType(type as ConfigurableNotificationCondition['type'], fields[0])
          )}
          options={conditionTypeOptions(condition.type).map((type) => ({
            value: type,
            label: conditionLabel(type),
          }))}
        />
      </div>
      <div style={triggerConditionCellStyle}>
        {'field' in condition && (
          <FieldSelect
            value={condition.field}
            fields={fields}
            t={t}
            onChange={(field) => onChange({ ...condition, field } as ConfigurableNotificationCondition)}
          />
        )}
        {condition.type === 'handoverEvent' && (
          <NotificationSelect
            value={condition.handover}
            onChange={(handover) => onChange({
              ...condition,
              handover: handover as typeof condition.handover,
            })}
            options={selectOptionsWithCurrentValue(condition.handover, handovers).map((handover) => ({
              value: handover,
              label: handoverLabel(t, handover),
            }))}
          />
        )}
        {condition.type === 'currentState' && (
          <NotificationSelect
            value={condition.state}
            onChange={(state) => onChange({ ...condition, state: state as typeof condition.state })}
            options={selectOptionsWithCurrentValue(condition.state, states).map((state) => ({
              value: state,
              label: state,
            }))}
          />
        )}
        {(condition.type === 'projectTypeIs' || condition.type === 'projectTypeIsNot') && (
          <NotificationSelect
            value={condition.projectType}
            onChange={(projectType) => onChange({
              ...condition,
              projectType: projectType as typeof condition.projectType,
            })}
            options={projectTypes.map((projectType) => ({
              value: projectType,
              label: enumLabel(t, projectType),
            }))}
          />
        )}
      </div>
      <div style={triggerConditionCellStyle}>
        {(condition.type === 'fieldEquals' || condition.type === 'fieldChangedToValue') && (
          <input
            value={condition.value}
            onChange={(event) => onChange({ ...condition, value: event.target.value })}
            style={inputStyle}
          />
        )}
        {condition.type === 'handoverEvent' && (
          <NotificationSelect
            value={condition.event}
            onChange={(event) => onChange({ ...condition, event: event as typeof condition.event })}
            options={selectOptionsWithCurrentValue(condition.event, handoverEvents).map((handoverEvent) => ({
              value: handoverEvent,
              label: handoverEventLabel(handoverEvent),
            }))}
          />
        )}
        {condition.type === 'elapsedSinceField' && (
          <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: 8 }}>
            <input
              type="number"
              min={1}
              value={condition.amount}
              onChange={(event) => onChange({ ...condition, amount: Number(event.target.value) })}
              style={inputStyle}
            />
            <NotificationSelect
              value={condition.unit}
              onChange={(unit) => onChange({ ...condition, unit: unit as typeof condition.unit })}
              options={timeUnitOptions}
            />
          </div>
        )}
        {condition.type === 'currentState' && (
          <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: 8 }}>
            <input
              type="number"
              min={0}
              value={condition.amount ?? 0}
              onChange={(event) => onChange({ ...condition, amount: Number(event.target.value) })}
              style={inputStyle}
            />
            <NotificationSelect
              value={condition.unit ?? 'days'}
              onChange={(unit) => onChange({
                ...condition,
                unit: unit as NonNullable<typeof condition.unit>,
              })}
              options={timeUnitOptions}
            />
          </div>
        )}
      </div>
      <div style={triggerActionCellStyle}>
        <ActionButton
          icon="delete"
          danger
          style={{ width: '100%', justifyContent: 'center', marginRight: 0 }}
          onClick={onRemove}
        >
          Entfernen
        </ActionButton>
      </div>
    </div>
  )
}

function RecipientEditor({
  recipient,
  userFields,
  roles,
  users,
  onChange,
  onRemove,
  t,
}: {
  recipient: ConfigurableNotificationRecipient
  userFields: string[]
  roles: string[]
  users: Array<{ key: string; value: string }>
  onChange: (recipient: ConfigurableNotificationRecipient) => void
  onRemove: () => void
  t: (key: string) => string
}) {
  return (
    <div style={recipientRowStyle}>
      <div style={triggerConditionCellStyle}>
        <NotificationSelect
          value={recipient.type}
          onChange={(type) => onChange(recipientForType(type as ConfigurableNotificationRecipient['type']))}
          options={recipientTypes.map((type) => ({
            value: type,
            label: recipientLabel(type),
          }))}
        />
      </div>
      <div style={triggerConditionCellStyle}>
        {recipient.type === 'email' && (
          <input
            value={recipient.email}
            onChange={(event) => onChange({ ...recipient, email: event.target.value })}
            style={inputStyle}
          />
        )}
        {recipient.type === 'user' && (
          <NotificationSelect
            value={String(recipient.userId)}
            onChange={(userId) => onChange({ ...recipient, userId: Number(userId) })}
            options={users
              .filter((user) => user.key !== '')
              .map((user) => ({
                value: user.key,
                label: user.value,
              }))}
          />
        )}
        {recipient.type === 'role' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 8, alignItems: 'center' }}>
            <NotificationSelect
              value={recipient.role}
              onChange={(role) => onChange({ ...recipient, role: role as typeof recipient.role })}
              options={roles.map((role) => ({
                value: role,
                label: roleLabel(role),
              }))}
            />
            <label style={roleRequirementLabelStyle}>
              <input
                type="checkbox"
                checked={recipient.requireTeamleitung === true}
                onChange={(event) => onChange({
                  ...recipient,
                  requireTeamleitung: event.target.checked,
                })}
                style={{ cursor: 'pointer' }}
              />
              Teamadmin
            </label>
          </div>
        )}
        {(recipient.type === 'referencedUser' || recipient.type === 'previousReferencedUser') && (
          <NotificationSelect
            value={recipient.field}
            onChange={(field) => onChange({ ...recipient, field: field as typeof recipient.field })}
            options={userFields.map((field) => ({
              value: field,
              label: fieldLabel(t, field),
            }))}
          />
        )}
      </div>
      <div style={triggerActionCellStyle}>
        <ActionButton
          icon="delete"
          danger
          style={{ width: '100%', justifyContent: 'center', marginRight: 0 }}
          onClick={onRemove}
        >
          Entfernen
        </ActionButton>
      </div>
    </div>
  )
}

export function NotificationSettings() {
  const { t } = useTranslation()
  const { authCtx } = useAuthContext()
  const userList = useUserListContext()
  const trpcUtils = trpc.useUtils()
  const metadata = trpc.notificationSettings.metadata.useQuery(undefined, {
    enabled: authCtx?.roles.includes('Admin') ?? false,
  })
  const definitions = trpc.notificationSettings.list.useQuery(undefined, {
    enabled: authCtx?.roles.includes('Admin') ?? false,
  })
  const createMutation = trpc.notificationSettings.create.useMutation({
    onSuccess: async () => {
      await trpcUtils.notificationSettings.list.invalidate()
      setEditing(null)
    },
  })
  const updateMutation = trpc.notificationSettings.update.useMutation({
    onSuccess: async () => {
      await trpcUtils.notificationSettings.list.invalidate()
      setEditing(null)
    },
  })
  const toggleEnabledMutation = trpc.notificationSettings.update.useMutation({
    onSuccess: async () => {
      await trpcUtils.notificationSettings.list.invalidate()
    },
  })
  const deleteMutation = trpc.notificationSettings.delete.useMutation({
    onSuccess: async () => {
      await trpcUtils.notificationSettings.list.invalidate()
    },
  })

  const rawFields = metadata.data?.fields ?? defaultFields
  const rawUserFields = metadata.data?.userFields ?? emptyFields
  const projectTypes = metadata.data?.projectTypes ?? fallbackProjectTypes
  const rawRoles = metadata.data?.roles
  const fields = useMemo(() => sortFieldsByLabel(rawFields, t), [rawFields, t])
  const userFields = useMemo(() => sortFieldsByLabel(rawUserFields, t), [rawUserFields, t])
  const roles = useMemo(() => mergeRecipientRoles(rawRoles ?? []), [rawRoles])
  const rootDefaultField = fields.includes(defaultFields[0]) ? defaultFields[0] : fields[0]
  const [editing, setEditing] = useState<NotificationForm | null>(null)
  const [previewProjectId, setPreviewProjectId] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [activeTemplateField, setActiveTemplateField] = useState<TemplateField>('bodyTemplate')
  const [fieldPickerOpen, setFieldPickerOpen] = useState(false)
  const [fieldPickerFilter, setFieldPickerFilter] = useState('')
  const subjectTemplateRef = useRef<HTMLInputElement | null>(null)
  const bodyTemplateRef = useRef<HTMLTextAreaElement | null>(null)

  const parsedEditing = useMemo(() => (
    editing == undefined ? undefined : ConfigurableNotificationDefinitionInputSchema.safeParse(editing)
  ), [editing])
  const previewAkteId = Number(previewProjectId)
  const preview = trpc.notificationSettings.preview.useQuery(
    {
      akteId: previewAkteId,
      definition: parsedEditing?.success ? parsedEditing.data : emptyForm(rootDefaultField),
    },
    {
      enabled: parsedEditing?.success === true && Number.isInteger(previewAkteId) && previewAkteId > 0,
      retry: false,
    }
  )

  const isAdmin = authCtx?.roles.includes('Admin') ?? false
  const rootRule = editing == undefined ? undefined : asRootRule(editing.trigger)
  const templateFieldOptions = useMemo(
    () => {
      const normalizedFilter = fieldPickerFilter.trim().toLowerCase()
      if (normalizedFilter.length === 0) {
        return fields
      }

      return fields.filter((field) => {
        const label = fieldLabel(t, field).toLowerCase()
        return field.toLowerCase().includes(normalizedFilter) || label.includes(normalizedFilter)
      })
    },
    [fieldPickerFilter, fields, t]
  )

  if (!isAdmin) {
    return (
      <Section>
        <Card>
          <CardHeader>E-Mail Benachrichtigungen</CardHeader>
          <CardBody>
            <Text style={textOnDarkStyle}>
              Fehlende Benutzer Berechtigungen, wenden sie sich an einen Administrator
            </Text>
          </CardBody>
        </Card>
      </Section>
    )
  }

  function updateEditing(patch: Partial<NotificationForm>) {
    setValidationError(null)
    setEditing((current) => (current == undefined ? current : { ...current, ...patch }))
  }

  function updateRootRule(nextRootRule: RootRule) {
    updateEditing({ trigger: nextRootRule })
  }

  function saveEditing() {
    if (editing == undefined) {
      return
    }
    setValidationError(null)
    const parsed = ConfigurableNotificationDefinitionInputSchema.safeParse(editing)
    if (!parsed.success) {
      const firstPath = parsed.error.issues[0]?.path.join('.')
      setValidationError(
        firstPath == undefined || firstPath.length === 0
          ? 'Speichern nicht moeglich. Bitte pruefe die Eingaben.'
          : `Speichern nicht moeglich. Bitte pruefe ${firstPath}.`
      )
      return
    }
    if (editing.id == undefined) {
      createMutation.mutate(parsed.data)
    } else {
      updateMutation.mutate({ ...parsed.data, id: editing.id })
    }
  }

  function insertTemplateToken(token: string) {
    if (editing == undefined) {
      return
    }

    const templateInput = activeTemplateField === 'subjectTemplate'
      ? subjectTemplateRef.current
      : bodyTemplateRef.current
    const currentValue = editing[activeTemplateField]
    const selectionStart = templateInput?.selectionStart ?? currentValue.length
    const selectionEnd = templateInput?.selectionEnd ?? selectionStart
    const nextValue = `${currentValue.slice(0, selectionStart)}${token}${currentValue.slice(selectionEnd)}`
    const nextCursorPosition = selectionStart + token.length

    updateEditing({ [activeTemplateField]: nextValue } as Partial<NotificationForm>)
    window.setTimeout(() => {
      const nextTemplateInput = activeTemplateField === 'subjectTemplate'
        ? subjectTemplateRef.current
        : bodyTemplateRef.current
      nextTemplateInput?.focus()
      nextTemplateInput?.setSelectionRange(nextCursorPosition, nextCursorPosition)
    }, 0)
  }

  function openFieldPicker() {
    setFieldPickerFilter('')
    setFieldPickerOpen(true)
  }

  function insertTemplateField(field: string) {
    insertTemplateToken(`{{field.${field}}}`)
  }

  return (
    <Section>
      <Row>
        <Col cols={12}>
          <Card>
            <CardHeader>E-Mail Benachrichtigungen</CardHeader>
            <CardBody>
              {(metadata.isLoading || definitions.isLoading || userList.isLoading) && <LoadingScreen active />}
              {!metadata.isLoading && !definitions.isLoading && !userList.isLoading && (
                <>
                  <View style={{ marginBottom: 60 }}>
                    <ActionButton
                      icon="plus"
                      prominent
                      onClick={() => {
                        setValidationError(null)
                        setEditing(emptyForm(rootDefaultField, nextNotificationKey(definitions.data)))
                      }}
                    >
                      Hinzufuegen
                    </ActionButton>
                  </View>

                  <table style={notificationOverviewTableStyle}>
                    <thead style={{ backgroundColor: dsv.colors.GpJouleGelb }}>
                      <tr>
                        <th style={{ ...tableHeaderCellStyle, textAlign: 'center', width: 70 }}>Aktiv</th>
                        <th style={{ ...tableHeaderCellStyle, width: 220 }}>Name</th>
                        <th style={{ ...tableHeaderCellStyle, width: '24%' }}>Trigger</th>
                        <th style={{ ...tableHeaderCellStyle, width: '17%' }}>An wen?</th>
                        <th style={{ ...tableHeaderCellStyle, width: '17%' }}>Betreff</th>
                        <th style={{ ...tableHeaderCellStyle, width: 150 }}>Zuletzt gesendet</th>
                        <th style={{ ...tableHeaderCellStyle, width: 240 }}>Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(definitions.data ?? []).map((definition) => (
                        <tr
                          key={definition.id}
                          style={{
                            backgroundColor: definition.enabled ? dsv.colors.connectGrau7 : dsv.colors.base3,
                          }}
                        >
                          <td style={tableCheckboxCellStyle}>
                            <input
                              type="checkbox"
                              checked={definition.enabled}
                              aria-label={`${definition.name} aktiv`}
                              onChange={(event) => {
                                toggleEnabledMutation.mutate(notificationUpdateInput(definition, event.target.checked))
                              }}
                              style={tableCheckboxStyle}
                            />
                          </td>
                          <td style={tableCellStyle}>
                            <span style={notificationNameTextStyle}>{definition.name}</span>
                          </td>
                          <td style={wrappingTableCellStyle}>
                            <span style={wrappingTableTextStyle}>{ruleSummary(definition.trigger, t)}</span>
                          </td>
                          <td style={wrappingTableCellStyle}>
                            <span style={wrappingTableTextStyle}>
                              {recipientsSummary(definition.recipients, userList.userById, t)}
                            </span>
                          </td>
                          <td style={wrappingTableCellStyle}>
                            <span style={wrappingTableTextStyle}>{definition.subjectTemplate}</span>
                          </td>
                          <td style={tableCellStyle}>
                            {formatDate(definition.lastSentAt)}
                          </td>
                          <td style={tableCellStyle}>
                            <div style={tableActionsStyle}>
                              <ActionButton
                                icon="edit"
                                style={tableActionButtonStyle}
                                onClick={() => {
                                  setValidationError(null)
                                  setEditing({ ...definition })
                                }}
                              >
                                Bearbeiten
                              </ActionButton>
                              <ActionButton
                                icon="delete"
                                danger
                                style={tableActionButtonStyle}
                                onClick={() => {
                                  if (window.confirm(`Benachrichtigung ${definition.name} loeschen?`)) {
                                    deleteMutation.mutate({ id: definition.id })
                                  }
                                }}
                              >
                                Loeschen
                              </ActionButton>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {(definitions.data ?? []).length === 0 && (
                        <tr>
                          <td colSpan={7} style={{ ...textOnDarkStyle, padding: 12 }}>
                            Keine Eintraege gefunden
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {editing != undefined && rootRule != undefined && metadata.data != undefined && !userList.isLoading && (
        <div
          style={modalBackdropStyle}
          onClick={() => {
            setValidationError(null)
            setEditing(null)
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            style={modalDialogStyle}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={editFormWrapperStyle}>
              <div style={formHeaderStyle}>
                <H2 style={{ ...sectionTitleStyle, fontSize: 24 }}>
                  {editing.id == undefined ? 'Benachrichtigung hinzufuegen' : 'Benachrichtigung bearbeiten'}
                </H2>
                <label style={activeToggleStyle}>
                  <input
                    type="checkbox"
                    checked={editing.enabled}
                    onChange={(event) => updateEditing({ enabled: event.target.checked })}
                    style={activeCheckboxStyle}
                  />
                  <Text style={textOnDarkStyle}>Aktiv</Text>
                </label>
              </div>
              <div style={formBodyStyle}>
                <div style={formSectionStyle}>
                  <div style={twoColumnFormGridStyle}>
                    <label style={labelColumnStyle}>
                      <Text style={fieldLabelStyle}>Notification ID</Text>
                      <input
                        value={editing.notificationKey}
                        readOnly
                        style={readOnlyInputStyle}
                      />
                    </label>
                    <label style={labelColumnStyle}>
                      <Text style={fieldLabelStyle}>Name</Text>
                      <input
                        value={editing.name}
                        onChange={(event) => updateEditing({ name: event.target.value })}
                        style={inputStyle}
                      />
                    </label>
                  </div>
                </div>

                <div style={formSectionStyle}>
                  <div style={sectionToolbarStyle}>
                    <H2 style={sectionTitleStyle}>Trigger</H2>
                    <Text style={textOnDarkStyle}>Alle Bedingungen verknuepfen mit</Text>
                    <NotificationSelect
                      value={rootRule.operator}
                      onChange={(operator) => updateRootRule({
                        ...rootRule,
                        operator: operator as 'all' | 'any',
                      })}
                      style={{ width: 120 }}
                      options={[
                        { value: 'all', label: 'UND' },
                        { value: 'any', label: 'ODER' },
                      ]}
                    />
                    <ActionButton
                      icon="plus"
                      onClick={() => updateRootRule({
                        ...rootRule,
                        conditions: [...rootRule.conditions, defaultCondition(rootDefaultField)],
                      })}
                    >
                      Bedingung
                    </ActionButton>
                  </div>
                  <div style={triggerConditionsListStyle}>
                    {rootRule.conditions.map((condition, index) => (
                      <ConditionEditor
                        key={index}
                        condition={condition}
                        fields={fields}
                        states={metadata.data?.states ?? []}
                        projectTypes={projectTypes}
                        handovers={metadata.data?.handovers ?? []}
                        handoverEvents={metadata.data?.handoverEvents ?? []}
                        t={t}
                        onChange={(nextCondition) => {
                          const nextConditions = [...rootRule.conditions]
                          nextConditions[index] = nextCondition
                          updateRootRule({ ...rootRule, conditions: nextConditions })
                        }}
                        onRemove={() => {
                          const nextConditions = rootRule.conditions.filter(
                            (_, conditionIndex) => conditionIndex !== index
                          )
                          updateRootRule({
                            ...rootRule,
                            conditions: nextConditions.length > 0
                              ? nextConditions
                              : [defaultCondition(rootDefaultField)],
                          })
                        }}
                      />
                    ))}
                  </div>
                </div>

                <div style={formSectionStyle}>
                  <div style={sectionToolbarStyle}>
                    <H2 style={sectionTitleStyle}>Empfaenger</H2>
                    <ActionButton
                      icon="plus"
                      onClick={() => updateEditing({ recipients: [...editing.recipients, defaultRecipient()] })}
                    >
                      Empfaenger
                    </ActionButton>
                  </div>
                  <div style={recipientRowsStyle}>
                    {editing.recipients.map((recipient, index) => (
                      <RecipientEditor
                        key={index}
                        recipient={recipient}
                        userFields={userFields}
                        roles={roles}
                        users={userList.allUserOptions}
                        t={t}
                        onChange={(nextRecipient) => {
                          const nextRecipients = [...editing.recipients]
                          nextRecipients[index] = nextRecipient
                          updateEditing({ recipients: nextRecipients })
                        }}
                        onRemove={() => {
                          const nextRecipients = editing.recipients.filter(
                            (_, recipientIndex) => recipientIndex !== index
                          )
                          updateEditing({
                            recipients: nextRecipients.length > 0 ? nextRecipients : [defaultRecipient()],
                          })
                        }}
                      />
                    ))}
                  </div>
                </div>

                <div style={formSectionStyle}>
                  <H2 style={sectionTitleStyle}>Inhalt</H2>
                  <div style={contentFormGridStyle}>
                    <label style={labelColumnStyle}>
                      <Text style={fieldLabelStyle}>Betreff</Text>
                      <input
                        ref={subjectTemplateRef}
                        value={editing.subjectTemplate}
                        onFocus={() => setActiveTemplateField('subjectTemplate')}
                        onChange={(event) => updateEditing({ subjectTemplate: event.target.value })}
                        style={inputStyle}
                      />
                    </label>
                    <label style={labelColumnStyle}>
                      <Text style={fieldLabelStyle}>Vorschau Projekt-ID</Text>
                      <input
                        value={previewProjectId}
                        onChange={(event) => setPreviewProjectId(event.target.value)}
                        style={inputStyle}
                      />
                    </label>
                  </div>
                  <label style={labelColumnStyle}>
                    <Text style={fieldLabelStyle}>Nachricht</Text>
                    <textarea
                      ref={bodyTemplateRef}
                      value={editing.bodyTemplate}
                      onFocus={() => setActiveTemplateField('bodyTemplate')}
                      onChange={(event) => updateEditing({ bodyTemplate: event.target.value })}
                      style={{ ...inputStyle, minHeight: 170 }}
                    />
                  </label>
                  <div style={tokenButtonListStyle}>
                    {metadata.data.templateTokens.map((token) => (
                      <button
                        key={token}
                        type="button"
                        title={`Fuegt ${token} ein`}
                        onClick={() => insertTemplateToken(token)}
                        style={tokenInsertButtonStyle}
                      >
                        {templateTokenLabel(token)}
                      </button>
                    ))}
                    <button
                      type="button"
                      title="Projektfeld auswaehlen"
                      onClick={openFieldPicker}
                      style={{
                        ...tokenInsertButtonStyle,
                        backgroundColor: dsv.colors.GpJouleGelb,
                      }}
                    >
                      Weitere Felder
                    </button>
                  </div>

                  {previewProjectId.length > 0 && (
                    <div style={previewBoxStyle}>
                      <H4>Vorschau</H4>
                      {preview.isLoading && <Text>Laedt...</Text>}
                      {preview.error != undefined && <Text>Keine Vorschau fuer diese Projekt-ID moeglich</Text>}
                      {preview.data != undefined && (
                        <>
                          <Text>Empfaenger: {preview.data.recipients.join(', ') || '-'}</Text>
                          <H4>{preview.data.subject}</H4>
                          <div dangerouslySetInnerHTML={{ __html: preview.data.html }} />
                        </>
                      )}
                    </div>
                  )}
                </div>

                {(createMutation.error != undefined || updateMutation.error != undefined) && (
                  <Text style={{ color: dsv.colors.error }}>
                    Speichern fehlgeschlagen. Notification ID muss eindeutig sein.
                  </Text>
                )}
                {validationError != undefined && (
                  <Text style={{ color: dsv.colors.error }}>
                    {validationError}
                  </Text>
                )}

                <div style={formFooterStyle}>
                  <ActionButton
                    icon="close"
                    onClick={() => {
                      setValidationError(null)
                      setEditing(null)
                    }}
                  >
                    Abbrechen
                  </ActionButton>
                  <ActionButton icon="save" primary prominent onClick={saveEditing}>
                    Speichern
                  </ActionButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {fieldPickerOpen && editing != undefined && (
        <div
          style={{ ...modalBackdropStyle, zIndex: 1100 }}
          onClick={(event) => {
            event.stopPropagation()
            setFieldPickerOpen(false)
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            style={fieldPickerDialogStyle}
            onClick={(event) => event.stopPropagation()}
          >
            <H2 style={{ ...headingOnDarkStyle, marginTop: 0 }}>
              Projektfeld einfuegen
            </H2>
            <Text style={textOnDarkStyle}>
              Ziel: {activeTemplateField === 'subjectTemplate' ? 'Betreff' : 'Nachricht'}
            </Text>
            <div style={{ marginTop: 16 }}>
              <label style={labelColumnStyle}>
                <Text style={fieldLabelStyle}>Feld suchen</Text>
                <input
                  value={fieldPickerFilter}
                  onChange={(event) => setFieldPickerFilter(event.target.value)}
                  style={inputStyle}
                  autoFocus
                />
              </label>
            </div>
            <div style={fieldPickerListStyle}>
              {templateFieldOptions.map((field) => {
                const label = fieldLabel(t, field)
                return (
                  <button
                    key={field}
                    type="button"
                    title={`Fuegt {{field.${field}}} ein`}
                    onClick={() => {
                      insertTemplateField(field)
                      setFieldPickerOpen(false)
                    }}
                    style={fieldPickerButtonStyle}
                  >
                    <span>{label}</span>
                    <span style={fieldPickerFieldNameStyle}>{field}</span>
                  </button>
                )
              })}
              {templateFieldOptions.length === 0 && (
                <Text style={textOnDarkStyle}>Keine Felder gefunden</Text>
              )}
            </div>
            <div style={{ marginTop: 18 }}>
              <ActionButton icon="close" onClick={() => setFieldPickerOpen(false)}>
                Schliessen
              </ActionButton>
            </div>
          </div>
        </div>
      )}
    </Section>
  )
}
